import { Patient, PatientPreferredDoctor, SecondaryContact } from '../../api';
import { VisitStatus } from '../../types';
import { Claim } from '../claim/types';
import { AsyncData } from '../core';
import { ServerResponse } from '../types';

export interface VisitsState {
  visits: AsyncData<Visit[]>;
  visitCodeToVisitIndex: { [code: string]: number };
  visitConfig: AsyncData<Properties>;
  availableTimeSlots: AsyncData<TimeSlot[]>;
  visitSideCardClaim?: Claim;
  cancelReasons?: AsyncData<CancelReasons>;
}

export interface RequestParams {
  asc: boolean;
  markets?: any;
  offset: number;
  limit: number;
  order_by: string;
  patient_id: string;
  search: string;
  status: string;
  visit_code: string;
}

export interface VisitAdminQueryResponse extends ServerResponse {
  requestParams: RequestParams;
  results: Visit[];
  total: number;
}

export interface CancelVisitResponse extends ServerResponse {
  visit: Visit;
}

export enum ConfirmationStateType {
  // IVR process has not started
  UNINITIATED = 'UNINITIATED',
  // IVR process has started
  AWAITING_RESPONSE = 'AWAITING_RESPONSE',
  // No response from multiple IVR attempts
  FAILED = 'FAILED',
  // Confirmed by the patient
  CONFIRMED = 'CONFIRMED',
  // Confirmed by operations
  CONFIRMED_MANUAL = 'CONFIRMED_MANUAL',
  // Confirmation process has been abandoned (visit cancelled etc.)
  STOPPED = 'STOPPED',
  // Visit is exempt from confirmation process
  EXEMPT = 'EXEMPT',
  // Visit was cancelled
  CANCELLED = 'CANCELLED',
}

// Backend type VisitViewAdmin
export interface Visit {
  address: string;
  addressId: string;
  addressLong: string;
  appliedSubscriptionContractName: string;
  appliedSubscriptionId: string;
  appsFlyerEventId: string;
  assistantAvatarUrl: string;
  assistantFirstName: string;
  assistantId: string;
  assistantLastName: string;
  assistantName: string;
  assistantPhone: string;
  assistantPrefix: string;
  assistantScheduleId: string;
  assistantSuffix: string;
  attributedToConciergeAgencyId?: string;
  availableActions: AvailableActions;
  billCode: string;
  billPromoCode: string;
  billStatus: string;
  bookNextDay: boolean;
  buildAssistantFullName: string;
  buildDoctorFullName: string;
  buildFullAddress: string;
  buildPatientFullName: string;
  buildUserAccountFullName: string;
  campaignId: string;
  campaignName: string;
  canBecomePreferredDoctor: true;
  canChangeDoctor: boolean;
  cancelReasonId: number;
  cancelReasonNote: string;
  cancellable: boolean;
  cancelled: boolean;
  cancelledBy: string;
  cardLabel: string;
  cardNumber: string;
  chargeAmount: number;
  city: string;
  completed: boolean;
  conciergeAgencyId: string;
  confirmationState: ConfirmationStateType;
  country: string;
  currentBillId: string;
  delayedPayment: boolean;
  deviceId: string;
  discountAmount: number;
  disposition?: string;
  doctorAvatarUrl: string;
  doctorBio: string;
  doctorEducation: string;
  doctorFirstName: string;
  doctorGender: string;
  doctorId: string;
  doctorLastName: string;
  doctorName: string;
  doctorPhone: string;
  doctorPrefix: string;
  doctorRating: number;
  doctorScheduleId: string;
  doctorSuffix: string;
  documentKey: string;
  drChronoPatientId: string;
  duplicateAddress?: boolean;
  endable: boolean;
  establishment: string;
  estimatedStartTime: number;
  gender: string;
  genderId: string;
  genderName: string;
  hasCampaign: boolean;
  hasDocument: boolean;
  id: string;
  instructions: string;
  insuranceActive: boolean;
  insuranceGroupId: string;
  insuranceMemberId: string;
  insuranceName: string;
  insurancePayerId: string;
  insurancePlanAppliedBenefitCode: string;
  insurancePlanAppliedBenefitType: string;
  insurancePlanReferenceId: string;
  insuranceResponseStatus: string;
  isNextVisit: boolean;
  isPatientActive: string;
  isPreferredDoctorForPatient: false;
  isVisitPD: boolean;
  isWaitlisted: boolean;
  latitudeAddress: number;
  latitudeDevice: number;
  legalGuardianPresent: boolean;
  longDateString: string;
  longerDateString: string;
  longerTimeString: string;
  longitudeAddress: number;
  longitudeDevice: number;
  marketCode: string;
  marketingChannelId: string;
  notes: string;
  originalAmount: number;
  originalTimeSlotId: string;
  originalVisitCode: string;
  paid: boolean;
  paidAmount: number;
  partner: string;
  patient: Patient;
  patientAvatarUrl: string;
  patientCancelledVisits: number;
  patientCompletedVisits: number;
  patientDob: string;
  patientEmail: string;
  patientFirstName: string;
  patientHasProxyNumber: boolean;
  patientId: string;
  patientInsuranceId: string;
  patientLastName: string;
  patientName: string;
  patientPCPStatus: string;
  patientPhone: string;
  paymentType: string;
  proxyNumber: string;
  ratingSentByUser: boolean;
  rebooking: string;
  recommendedDoctor: string;
  recommendedTime: number;
  refundable: boolean;
  refunded: boolean;
  refundedAmount: number;
  relationshipId: string;
  relationshipName: string;
  requestedTimeSlotFormatted: string;
  reschedules: number;
  scheduledTime: number;
  scheduledTimeFormatted: string;
  selectedDoctorFirstName: string;
  selectedDoctorId: string;
  selectedDoctorLastName: string;
  shortDateString: string;
  shortTimeString: string;
  startable: boolean;
  state: string;
  status: VisitStatus;
  subServiceCode?: string;
  subStatus: string;
  symptoms: string;
  systemVisitDateFormatted: string;
  systemVisitShortTimeFormatted: string;
  systemVisitTimeFormatted: string;
  testAdmin: string;
  timeCancelledVisit: number;
  timeEndedVisit: number;
  timeProviderEnRoute: number;
  timeProviderExpectedArrival: number;
  timeRequested: number;
  timeSlotId: string;
  timeStartedVisit: number;
  timelineViewable: boolean;
  timeslotEndTime: number;
  timeslotStartTime: number;
  timezone: string;
  unit: string;
  updatedAt: string;
  userAccountAvatarUrl: string;
  userAccountFirstName: string;
  userAccountId: string;
  userAccountLastName: string;
  userAccountType: string;
  username: string;
  visitCode: string;
  visitDateFormatted: string;
  visitDateRequested: string;
  visitRating: string;
  visitService: any;
  visitServiceCode: string;
  visitServiceDescription: string;
  visitServiceId: string;
  visitServiceName: string;
  visitServiceShortName: string;
  visitShortTimeFormatted: string;
  visitStatus: string;
  visitTimeFormatted: string;
  waitingToHappen: boolean;
  zipcode: string;
}

// Backend type WebVisit
export interface WebVisit {
  accountOwnerName: string;
  address: string;
  addressId: string;
  addressLatitude: number;
  addressLong: string;
  addressLongitude: number;
  assistantAvatarUrl: string;
  assistantId: string;
  assistantLatitude: number;
  assistantLongitude: number;
  assistantName: string;
  assistantPhone: string;
  billId: string;
  billStatus: string;
  canBecomePreferredDoctor: boolean;
  canCancel: boolean;
  canChangeDoctor: boolean;
  canEnd: boolean;
  canReschedule: boolean;
  canStart: boolean;
  cardLabel: string;
  cardNumber: string;
  chargeAmount: number;
  city: string;
  country: string;
  dateCancelled: string;
  dateCompleted: string;
  dateCompletedTimestamp: number;
  dateRequested: string;
  dateRequestedTimestamp: number;
  deviceLatitude: number;
  deviceLongitude: number;
  doctorAvatarUrl: string;
  doctorBio: string;
  doctorEducation: string;
  doctorId: string;
  doctorLatitude: number;
  doctorLongitude: number;
  doctorName: string;
  doctorPhone: string;
  estimatedStartTime: number;
  groupType: string;
  hasCampaign: boolean;
  historyTitle: string;
  hubActive: boolean;
  instructions: string;
  insuranceGroupId: string;
  insuranceMemberId: string;
  insuranceName: string;
  insurancePayerId: string;
  insurancePlanName: string;
  insurancePlanReferenceId: string;
  insuranceResponseStatus: string;
  isPreferredDoctorForPatient: boolean;
  isWaitlisted: boolean;
  marketCode: string;
  marketName: string;
  minorVisit: boolean;
  patientCancelledVisits: number;
  patientCompletedVisits: number;
  patientDob: string;
  patientEmail: string;
  patientGender: string;
  patientId: string;
  patientName: string;
  patientPhone: string;
  patientPreferredDoctor: PatientPreferredDoctor;
  patientRelationship: string;
  patientSecondaryContact: SecondaryContact;
  promoCode: string;
  ratingStatus: boolean;
  refunded: boolean;
  rescheduleLabel: string;
  scheduleFor: number;
  showCarePlan: boolean;
  showReminderForCarePlanConsents: boolean;
  state: string;
  symptoms: string;
  systemVisitDateFormatted: string;
  systemVisitShortTimeFormatted: string;
  systemVisitTimeFormatted: string;
  timeSlotEndTime: number;
  timeSlotId: string;
  timeSlotStartTime: number;
  timezone: string;
  unit: string;
  userAccountEmail: string;
  userAccountId: string;
  userAccountType: string;
  usingInsurance: boolean;
  visitAmountCharge: string;
  visitAmountOriginal: string;
  visitAmountRefunded: string;
  visitCode: string;
  visitDateFormatted: string;
  visitDiscount: string;
  visitFilesRequired: boolean;
  visitId: string;
  visitServiceCode: string;
  visitServiceDescription: string;
  visitServiceId: string;
  visitShortTimeFormatted: string;
  visitStatus: string;
  visitTimeFormatted: string;
  visitWithPreferredDoctor: boolean;
  zipcode: string;
}

export interface AvailableActions {
  startVisit: boolean;
  changeDoctor: boolean;
  endVisit: boolean;
  addInsurance: boolean;
  viewTimeline: boolean;
  cancelVisit: boolean;
  refund: boolean;
}

export interface VisitLogResponse {
  total: number;
  results: VisitLogEntry[];
}

export interface VisitLogEntry {
  frozen?: boolean;
  userAccountFullName: string;
  notificationId?: string;
  log: string;
  visitCode?: string;
  id: string;
  createdByRequestId?: string;
  createdByUserAccountId?: string;
  updatedByUserAccountId?: string;
  lastUpdatedByRequestId?: string;
  lastUpdatedAt?: number;
  updatedAt?: number;
  createdAt: number;
  valid?: boolean;
  idName?: string;
  compositeKeys?: string;
  new?: boolean;
  modified?: boolean;
  status?: string;
}

export interface VisitConfigServerResponse extends ServerResponse {
  properties: Properties;
}

export interface CancelReason {
  id: string;
  label: string;
  patientLabel: string;
}

export interface CancelReasons {
  [category: string]: CancelReason[];
}

export interface Properties {
  payers: Payer[];
}

export interface Payer {
  longName: string;
  logoUrl?: string | null;
  shortName: string;
  eligiblePayerCode: string;
  payerOfflineBehavior: string;
  availableToSelect: boolean;
  requestGroupId: boolean;
  id: string;
  createdAt: string | null;
  updatedAt: string | null;
  lastUpdatedAt: number | null;
  athenaPlans:
    | {
        packageId: string;
        name: string;
      }[]
    | null;
}

export interface TimeSlot {
  id: string;
  disabled: boolean;
  available: boolean;
  startTime: number;
  endTime: number;
  shortDateString: string;
  longDateString: string;
  longerDateString: string;
  shortTimeString: string;
  longTimeString: string;
  longerTimeString: string;
  timezone: string;
}
